import React, { useEffect, useState } from "react";
import { Formik, FieldArray, Field, useFormikContext } from "formik";
import { PlusIcon as PlusIcon } from "@heroicons/react/24/solid";
import {
  TrashIcon as TrashIconMini,
  PlusCircleIcon,
} from "@heroicons/react/20/solid";
import { useQuery, gql, useMutation } from "@apollo/client";
// import { ToastContainer, toast } from "react-toastify";
import _ from "lodash";
import DiagnosisSearchNSelect from "./request/DiagnosisSearchNSelect";
import supportingInfoElements from "./request/SupportingInfoElement";
import {
  items as sites,
  dentalItems as oralItems,
  investigationResult,
} from "../components/request/BodySites";
import ItemCodesSearchNSelect from "./request/ItemCodesSearchNSelect";
import { MdAttachFile, MdDownload, MdEdit } from "react-icons/md";
import DentalTeethSelectModal from "./DentalTeethSelectModal";
import { Button, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { REFERRAL_IN_SOURCE } from "../../../utils/referralSupportingInfo";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import InboxIcon from "@mui/icons-material/MoveToInbox";
import MailIcon from "@mui/icons-material/Mail";
import { string } from "yup";
import MorphologySearch from "./request/MorphologySearch";
import { useTranslation } from "react-i18next";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import AppTable from "../../../shared/AppTable";
import ValidationErrors from "../../../components/ValidationErrors";

function ValueCellEditor({ row, index, handleChange, setFieldValue, t }) {
  const [openModal, setOpenModal] = useState(false);

  if (!("value" in row)) {
    return (
      <div className="flex justify-center items-center w-full text-center">
        <span className="text-gray-400 font-bold text-sm tracking-widest">--</span>
      </div>
    );
  }

  const isTextareaCategory = [
    "chief-complaint",
    "history-of-present-illness",
    "patient-history",
    "physical-examination",
    "treatment-plan",
    "info",
  ].includes(row.category);

  if (!isTextareaCategory) {
    return (
      <CustomFilterInput
        type="text"
        placeholder={t("Value")}
        name={`supportingInfos.${index}.value`}
        value={row.value || ""}
        onChange={handleChange}
      />
    );
  }

  const val = row.value || "";
  const isLong = val.length > 35 || val.includes("\n");

  return (
    <div className="flex flex-col gap-1 w-full">
      <CustomFilterInput
        type="textarea"
        placeholder={t("Value")}
        name={`supportingInfos.${index}.value`}
        value={val}
        onChange={handleChange}
      />
      {isLong && (
      <button
        type="button"
        className="text-[11px] font-bold text-blue-600 hover:text-blue-800 hover:underline cursor-pointer bg-transparent border-0 p-0 self-end"
        onClick={() => setOpenModal(true)}
      >
        {t("View more")}
      </button>
      )}

      {openModal && (
        <Dialog open={openModal} onClose={() => setOpenModal(false)} maxWidth="sm" fullWidth>
          <DialogTitle className="!text-sm !font-bold !py-3 flex items-center justify-between border-b">
            <span className="capitalize">
              {t("Edit")} - {row.category?.replace(/-/g, " ")}
            </span>
            <button
              type="button"
              onClick={() => setOpenModal(false)}
              className="text-gray-400 hover:text-gray-600 font-bold text-base px-2"
            >
              ✕
            </button>
          </DialogTitle>
          <DialogContent className="!py-4">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5 capitalize">
              {row.category?.replace(/-/g, " ")} {t("Value / Clinical Notes")}
            </label>
            <textarea
              name={`supportingInfos.${index}.value`}
              value={val}
              onChange={handleChange}
              rows={8}
              placeholder={t("Enter detailed value or clinical notes...")}
              className="w-full p-2.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
          </DialogContent>
          <DialogActions className="!px-4 !py-2.5 border-t">
            <Button
              onClick={() => setOpenModal(false)}
              variant="contained"
              size="small"
              className="!bg-blue-600 !text-white !capitalize !text-xs"
            >
              {t("Done")}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}

const reasonForVisitOptions = [
  { id: "referral", name: "Referral" },
];

function SupportingInfoForm({ onChange, refId }) {
  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    setFieldValue,
  } = useFormikContext();
  const { t, i18n } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const toggleDrawer = (newOpen) => () => {
    setOpen(newOpen);
  };

  const [showTeethSelectModal, setShowTeethSelectModal] = useState(false);
  const [supportingInfo, setSupportingInfo] = useState("");
  const hasValidation = (row) =>
    row?.has_validation_error === true ||
    (Array.isArray(row?.validation_errors) && row.validation_errors.length > 0);

  const addData = (values) => {
    let data = _.cloneDeep(values);
    // createInsurancePlan({ variables: { input: data } });
  };

  //method to transform category value to kebab-case
  const transformedData = () => {
    let data = [];

    supportingInfoElements(t).map((obj) => {
      // iterate over each object and transform its category property
      data.push({
        ...obj, // spread operator to copy all other properties
        object: {
          ...obj.object, // copy all other properties of nested object
          category: obj.object.category.toLowerCase().replace(/\s+/g, "-"),
        },
      });
    });

    return data;
  };

  function downloadBase64File(base64Data, fileName, fileType) {
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: fileType });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
  }

  const handleImageChange = (e, setFieldValue, index) => {
    const file = e.target.files[0];
    const reader = new FileReader();

    reader.onloadend = () => {
      const base64String = reader.result
        .replace("data:", "")
        .replace(/^.+,/, "");
      setFieldValue(`supportingInfos.${index}.attachment`, base64String);
      setFieldValue(`supportingInfos.${index}.fileName`, file.name);
      setFieldValue(`supportingInfos.${index}.fileType`, file.type);
    };

    reader.readAsDataURL(file);
  };

  const missingToothReasons = [
    { id: "extraction", name: "Extraction" },
    { id: "congenital", name: "Congenital" },
    { id: "unknown", name: "Unknown" },
    { id: "other", name: "Other" },
  ];

  const getRequiredItemName = (desiredId) => {
    const result = _.find(oralItems, { id: desiredId });
    return result.name;
  };

  const getRequiredInvertigationName = (desiredId) => {
    const result = _.find(investigationResult, { id: desiredId });
    return result.name;
  };

  const vitalSignReasonLOVs = {
    "vital-sign-height": [{ id: "999.9", name: "Not Measured" }],
    "vital-sign-weight": [{ id: "999.9", name: "Not Collected" }],
    "vital-sign-systolic": [
      { id: "888", name: "Not measurable" },
      { id: "999", name: "Unknown" },
    ],
    "vital-sign-diastolic": [
      { id: "888", name: "Not measurable" },
      { id: "999", name: "Unknown" },
    ],
    temperature: [
      { id: "88", name: "Not Obtained" },
      { id: "99", name: "Unknown" },
    ],
    pulse: [
      { id: "997", name: "Cardiac Arrest" },
      { id: "998", name: "Not Recorded" },
      { id: "999", name: "Not Stated" },
    ],
    "respiratory-rate": [
      { id: "00", name: "Artificial Ventilation" },
      { id: "88", name: "Not Obtained" },
      { id: "99", name: "Unknown" },
    ],
    "oxygen-saturation": [
      { id: "888", name: "Not Obtained" },
      { id: "999", name: "Unknown" },
    ],
  };

  useEffect(() => {
    const categoriesToMonitor = [
      "vital-sign-weight",
      "vital-sign-diastolic",
      "vital-sign-height",
      "vital-sign-systolic",
      "pulse",
      "oxygen-saturation",
      "temperature",
      "respiratory-rate",
    ];

    const defaultReasons = {
      "vital-sign-height": "999.9",
      "vital-sign-weight": "999.9",
      "vital-sign-systolic": "888",
      "vital-sign-diastolic": "888",
      "temperature": "88",
      "pulse": "998",
      "respiratory-rate": "88",
      "oxygen-saturation": "888",
    };

    values.supportingInfos.forEach((info, index) => {
      if (categoriesToMonitor.includes(info.category)) {
        const hasValue = info.value !== null && info.value !== undefined && String(info.value).trim() !== "";
        
        if (hasValue) {
          if (info.reason !== "" && info.reason !== null) {
            setFieldValue(`supportingInfos.${index}.reason`, "");
          }
        }
        else {
          const defaultReason = defaultReasons[info.category];
          if (defaultReason && info.reason !== defaultReason) {
            setFieldValue(`supportingInfos.${index}.reason`, defaultReason);
          }
        }
      }
    });
  }, [values.supportingInfos, setFieldValue]);

  useEffect(() => {
    if (!values?.supportingInfos || !Array.isArray(values.supportingInfos)) return;
    const defaultDate = values?.dateOrdered || new Date().toISOString().substring(0, 10);

    values.supportingInfos.forEach((info, index) => {
      if ("startDate" in info && (!info.startDate || info.startDate === "")) {
        setFieldValue(`supportingInfos.${index}.startDate`, defaultDate);
      }
      if ("endDate" in info && (!info.endDate || info.endDate === "")) {
        setFieldValue(`supportingInfos.${index}.endDate`, defaultDate);
      }
    });
  }, [values.dateOrdered, values.supportingInfos?.length, setFieldValue]);

  useEffect(() => {
    const { claimType, isNewBorn, supportingInfos } = values;

    let newItemsToPush = [];

    const requiredCategoriesByType = {
      professional: [
        "chief-complaint",
        "vital-sign-weight",
        "vital-sign-diastolic",
        "vital-sign-height",
        "vital-sign-systolic",
        "treatment-plan",
        "history-of-present-illness",
        "patient-history",
        "physical-examination",
        "pulse",
        "oxygen-saturation",
        "temperature",
        "respiratory-rate",
        "investigation-result",
      ],
      institutional: [
        "chief-complaint",
        "vital-sign-weight",
        "vital-sign-diastolic",
        "vital-sign-height",
        "vital-sign-systolic",
        "treatment-plan",
        "history-of-present-illness",
        "patient-history",
        "physical-examination",
        "estimated-Length-of-Stay",
        "pulse",
        "oxygen-saturation",
        "temperature",
        "respiratory-rate",
        "investigation-result",
      ],
      oral: [
        "chief-complaint",
        "treatment-plan",
        "history-of-present-illness",
        "patient-history",
        "physical-examination",
        "investigation-result",
      ],
      vision: [
        "treatment-plan",
        "history-of-present-illness",
        "patient-history",
        "physical-examination",
        "investigation-result",
      ],
      pharmacy: [
        "treatment-plan",
        "history-of-present-illness",
        "patient-history",
        "physical-examination",
        "investigation-result",
      ],
    };

    const categoriesToPush = requiredCategoriesByType[claimType] || [];

    categoriesToPush.forEach((category) => {
      const itemToPush = supportingInfoElements(t).find(
        (item) => item.object.category === category,
      );

      if (
        itemToPush &&
        !supportingInfos.some((info) => info.category === category)
      ) {
        newItemsToPush.push(itemToPush.object);
      }
    });

    if (claimType === "institutional" && isNewBorn) {
      const birthWeightItem = supportingInfoElements(t).find(
        (item) => item.object.category === "birth-weight",
      );

      const admissionWeightItem = supportingInfoElements(t).find(
        (item) => item.object.category === "admission-weight",
      );

      if (
        birthWeightItem &&
        !supportingInfos.some((info) => info.category === "birth-weight")
      ) {
        newItemsToPush.push(birthWeightItem.object);
      }

      if (
        admissionWeightItem &&
        !supportingInfos.some((info) => info.category === "admission-weight")
      ) {
        newItemsToPush.push(admissionWeightItem.object);
      }
    }

    if (newItemsToPush.length > 0) {
      const autoReferral = supportingInfos.filter(
        (info) => info?.source === REFERRAL_IN_SOURCE,
      );
      const otherSupportingInfo = supportingInfos.filter(
        (info) => info?.source !== REFERRAL_IN_SOURCE,
      );

      setFieldValue("supportingInfos", [
        ...otherSupportingInfo,
        ...newItemsToPush,
        ...autoReferral,
      ]);
    }
  }, [values.claimType, values.isNewBorn]);

  return (
    <div className="personal-data !p-2 !mb-2">
      {/* 1. Header Section */}
      <div className="personal-title !mb-2 flex items-center gap-2">
        <div className="inventory-svg">
          <svg width="26" height="26" viewBox="0 0 34 34" fill="none">
            <rect width="34" height="34" rx="8" fill="#F4F6F8"></rect>
            <path
              d="M9.25 11H11.25V14H21.25V11H23.25V16H25.25V11C25.25 9.9 24.35 9 23.25 9H19.07C18.65 7.84 17.55 7 16.25 7C14.95 7 13.85 7.84 13.43 9H9.25C8.15 9 7.25 9.9 7.25 11V25C7.25 26.1 8.15 27 9.25 27H15.25V25H9.25V11ZM16.25 9C16.8 9 17.25 9.45 17.25 10C17.25 10.55 16.8 11 16.25 11C15.7 11 15.25 10.55 15.25 10C15.25 9.45 15.7 9 16.25 9Z"
              fill="#0070c2"
            ></path>
            <path
              d="M25.25 17.5L19.76 23L16.75 20L15.25 21.5L19.76 26L26.75 19L25.25 17.5Z"
              fill="#0070c2"
            ></path>
          </svg>
        </div>
        <div className="personal-head">
          <span>{t("Supporting Info")}</span>
        </div>
      </div>

      {/* 2. FieldArray Section */}
      <FieldArray name="supportingInfos">
        {({ push, remove }) => {
          const columns = [
            {
              header: t("No."),
              width: "60px",
              render: (row, index) => (
                <div className="flex items-center gap-1.5 min-w-[50px]">
                  <div
                    title={t("Delete")}
                    className="cursor-pointer flex items-center justify-center text-red-500 hover:text-red-700 transition-colors p-1 hover:bg-red-50 rounded"
                    onClick={() => {
                      const idx = values.supportingInfos.indexOf(row);
                      if (idx > -1) remove(idx);
                      else remove(index);
                    }}
                  >
                    <TrashIconMini className="h-4 w-4 text-red-500 hover:text-red-700" />
                  </div>
                  <span className="text-[12px] font-bold text-gray-700">{index + 1}</span>
                </div>
              ),
            },
            {
              header: t("Category"),
              render: (row) => (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-[11.5px] font-semibold bg-slate-100 text-slate-700 capitalize border border-slate-200">
                  {row.category?.replace(/-/g, " ")}
                </span>
              ),
            },
            {
              header: t("Code"),
              render: (row, index) => {
                const categoriesWithCode = [
                  "chief-complaint",
                  "lab-test",
                  "missingtooth",
                  "onset",
                  "reason-for-visit",
                  "morphology",
                  "investigation-result",
                ];

                const shouldShowCode = categoriesWithCode.includes(row.category);

                if (!shouldShowCode) {
                  return (
                    <div className="flex justify-center items-center w-full text-center">
                      <span className="text-gray-400 font-bold text-sm tracking-widest">--</span>
                    </div>
                  );
                }

                return (
                  <div className="min-w-[200px]">
                    {row.code && row.category !== "reason-for-visit" && (
                      <Tooltip title={`${row.code_text} - ${row.code}`}>
                        <div className="text-[11px] font-bold text-blue-600 mb-1">
                          {row.code} ({row.code_text?.slice(0, 20)}...)
                        </div>
                      </Tooltip>
                    )}

                    {row.category === "lab-test" ? (
                      <ItemCodesSearchNSelect
                        typeName="loinc"
                        setFieldValue={(val) => {
                          setFieldValue(`supportingInfos.${index}.code`, val.code);
                          setFieldValue(
                            `supportingInfos.${index}.codeText`,
                            val.description,
                          );
                        }}
                      />
                    ) : row.category === "morphology" ? (
                      <MorphologySearch
                        values={row}
                        setFieldValue={(val) => {
                          setFieldValue(`supportingInfos.${index}.code`, val.Code);
                          setFieldValue(
                            `supportingInfos.${index}.codeText`,
                            val.Display,
                          );
                        }}
                      />
                    ) : row.category === "investigation-result" ? (
                      <CustomFilterInput
                        isSelect
                        placeholder={t("Please Select")}
                        options={investigationResult}
                        value={row.code}
                        onChange={(e) => {
                          const val = e.target.value;
                          const found = _.find(investigationResult, { id: val });
                          setFieldValue(`supportingInfos.${index}.code`, val);
                          setFieldValue(`supportingInfos.${index}.codeText`, found?.name);
                        }}
                      />
                    ) : row.category === "reason-for-visit" ? (
                      <CustomFilterInput
                        isSelect
                        placeholder={t("Please Select")}
                        options={reasonForVisitOptions}
                        value={row.code}
                        onChange={(e) => {
                          setFieldValue(
                            `supportingInfos.${index}.code`,
                            e.target.value,
                          );
                          setFieldValue(
                            `supportingInfos.${index}.codeText`,
                            "Reason for visit",
                          );
                        }}
                      />
                    ) : row.category === "missingtooth" ? (
                      <div className="flex gap-1">
                        <CustomFilterInput
                          isSelect
                          placeholder={t("Please Select")}
                          options={oralItems}
                          value={row.code}
                          onChange={(e) => {
                            const val = e.target.value;
                            const found = _.find(oralItems, { id: val });
                            setFieldValue(`supportingInfos.${index}.code`, val);
                            setFieldValue(`supportingInfos.${index}.codeText`, found?.name);
                          }}
                        />
                        <Button
                          size="small"
                          onClick={() => {
                            setShowTeethSelectModal(true);
                            setSupportingInfo(index);
                          }}
                          variant="contained"
                          style={{
                            backgroundImage: "linear-gradient(83deg, #0070C2)",
                          }}
                          className="!rounded-md !self-end !min-w-fit !px-3"
                        >
                          {t("Select")}
                        </Button>
                      </div>
                    ) : (
                      <DiagnosisSearchNSelect
                        category={row.category}
                        values={row}
                        setFieldValue={(val) => {
                          setFieldValue(`supportingInfos.${index}.code`, val.CodeId);
                          setFieldValue(
                            `supportingInfos.${index}.codeText`,
                            val.AsciiDescription,
                          );
                        }}
                      />
                    )}
                  </div>
                );
              },
            },
            /*
            {
              header: t("Timing"),
              render: (row, index) => {
                const hasTiming = "startDate" in row || "endDate" in row;
                if (!hasTiming) {
                  return (
                    <div className="flex justify-center items-center w-full text-center">
                      <span className="text-gray-400 font-bold text-sm tracking-widest">--</span>
                    </div>
                  );
                }
                return (
                  <div className="flex gap-1 min-w-[180px]">
                    {"startDate" in row && (
                      <CustomFilterInput
                        type="date"
                        label={t("Start")}
                        name={`supportingInfos.${index}.startDate`}
                        value={row.startDate || values?.dateOrdered || ""}
                        onChange={handleChange}
                      />
                    )}
                    {"endDate" in row && (
                      <CustomFilterInput
                        type="date"
                        label={t("End")}
                        name={`supportingInfos.${index}.endDate`}
                        value={row.endDate || values?.dateOrdered || ""}
                        onChange={handleChange}
                      />
                    )}
                  </div>
                );
              },
            },
            */
            {
              header: t("Value"),
              render: (row, index) => {
                const isTextareaCategory = [
                  "chief-complaint",
                  "history-of-present-illness",
                  "patient-history",
                  "physical-examination",
                  "treatment-plan",
                  "info",
                ].includes(row.category);

                const element = (
                  <ValueCellEditor
                    row={row}
                    index={index}
                    handleChange={handleChange}
                    setFieldValue={setFieldValue}
                    t={t}
                  />
                );

                if (isTextareaCategory) {
                  return { content: element, colSpan: 3 };
                }
                return element;
              },
            },
            {
              header: t("Attachment"),
              render: (row, index) => {
                const isTextareaCategory = [
                  "chief-complaint",
                  "history-of-present-illness",
                  "patient-history",
                  "physical-examination",
                  "treatment-plan",
                  "info",
                ].includes(row.category);

                if (isTextareaCategory) {
                  return null;
                }

                if (!("attachment" in row)) {
                  return (
                    <div className="flex justify-center items-center w-full text-center">
                      <span className="text-gray-400 font-bold text-sm tracking-widest">--</span>
                    </div>
                  );
                }
                return (
                  <div className="min-w-[150px]">
                    {!row?.fileName ? (
                      <input
                        type="file"
                        className="text-[12px]"
                        onChange={(e) => handleImageChange(e, setFieldValue, index)}
                      />
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <div className="flex items-center gap-1 text-blue-600">
                          <MdAttachFile />
                          <span className="text-[11px] truncate w-20">
                            {row?.fileName}
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <MdDownload
                            className="text-gray-600 hover:text-blue-600 cursor-pointer transition-colors"
                            onClick={() => {
                              if (row?.imageUrl && row?.imageUrl !== "") {
                                const a = document.createElement("a");
                                a.href = row?.imageUrl;
                                a.download = row?.fileName || "download";
                                a.target = "_blank";
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                              } else if (row?.attachment) {
                                downloadBase64File(
                                  row?.attachment,
                                  row?.fileName,
                                  row?.fileType,
                                );
                              }
                            }}
                          />
                          <label
                            htmlFor={`edit-image-${index}`}
                            className="cursor-pointer"
                          >
                            <MdEdit className="text-gray-600 hover:text-green-600 transition-colors" />
                          </label>
                          <input
                            id={`edit-image-${index}`}
                            type="file"
                            className="hidden"
                            onChange={(e) => handleImageChange(e, setFieldValue, index)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              },
            },
            {
              header: t("Reason"),
              render: (row, index) => {
                if (!("reason" in row)) {
                  return (
                    <div className="flex justify-center items-center w-full text-center">
                      <span className="text-gray-400 font-bold text-sm tracking-widest">--</span>
                    </div>
                  );
                }
                return (
                  <div className="min-w-[130px]">
                    <CustomFilterInput
                      isSelect
                      placeholder={t("Reason")}
                      options={
                        row.category === "missingtooth"
                          ? missingToothReasons
                          : vitalSignReasonLOVs[row.category] || []
                      }
                      value={row.reason}
                      onChange={handleChange}
                      name={`supportingInfos.${index}.reason`}
                    />
                  </div>
                );
              },
            },
          ];

          return (
            <>
              {/* Buttons List to Add Items */}
              <div className="mb-2">
                <Box className="w-full border border-gray-200 rounded-md p-2">
                  <List className="flex flex-wrap gap-x-1.5 gap-y-1.5 !m-0 !p-0">
                    {supportingInfoElements(t).map((item) => (
                      <ListItem
                        key={item.id}
                        disablePadding
                        className="max-w-fit border border-gray-200 rounded-md bg-white hover:bg-gray-50 transition-colors"
                      >
                        <ListItemButton
                          onClick={() => {
                            const defaultDate =
                              values?.dateOrdered ||
                              new Date().toISOString().substring(0, 10);
                            const itemObj = { ...item.object };
                            if (
                              "startDate" in itemObj &&
                              (!itemObj.startDate || itemObj.startDate === "")
                            ) {
                              itemObj.startDate = defaultDate;
                            }
                            if (
                              "endDate" in itemObj &&
                              (!itemObj.endDate || itemObj.endDate === "")
                            ) {
                              itemObj.endDate = defaultDate;
                            }
                            push(itemObj);
                          }}
                          className="!py-1 !px-3 rounded-md"
                        >
                          <ListItemText
                            primary={item.name}
                            primaryTypographyProps={{
                              className:
                                "!text-[11.5px] !font-bold text-gray-600",
                            }}
                            className="!m-0"
                          />
                          <ListItemIcon className="!min-w-fit ml-2.5 text-gray-500">
                            {React.cloneElement(item.icon, {
                              style: { fontSize: "13px" },
                            })}
                          </ListItemIcon>
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              </div>

              {/* 3. The New AppTable (Replacement for <table>) */}
              <div className="supporting-info-app-table">
                <style>{`
                  .supporting-info-app-table table thead th {
                    font-size: 11.5px !important;
                    color: #334155 !important;
                    text-transform: uppercase !important;
                    font-weight: 700 !important;
                    background-color: #f8fafc !important;
                    border-bottom: 2px solid #e2e8f0 !important;
                    padding-top: 3px !important;
                    padding-bottom: 3px !important;
                  }
                  .supporting-info-app-table table tbody td {
                    font-size: 12px !important;
                    color: #334155 !important;
                    padding-top: 5px !important;
                    padding-bottom: 5px !important;
                    vertical-align: middle !important;
                  }
                  .supporting-info-app-table table tbody td .MuiSvgIcon-root {
                    font-size: 18px !important;
                  }
                `}</style>
                <AppTable
                  columns={columns}
                  data={values.supportingInfos || []}
                  actions={false}
                  renderRowBelow={(row) =>
                    hasValidation(row) ? (
                      <ValidationErrors
                        validationErrors={row.validation_errors}
                        compact
                      />
                    ) : null
                  }
                  getRowSx={(row) => ({
                    backgroundColor: hasValidation(row)
                      ? "#FEF3C7"
                      : "transparent",
                    "&:hover": {
                      backgroundColor: hasValidation(row)
                        ? "#FDE68A !important"
                        : "rgba(0, 0, 0, 0.04)",
                    },
                  })}
                />
              </div>
            </>
          );
        }}
      </FieldArray>

      {errors.supportingInfos && touched.supportingInfos && (
        <div className="mt-2 p-2 border border-red-200 bg-red-50 rounded text-[11px] text-red-600">
          {typeof errors.supportingInfos === 'string' ? (
            <div>{errors.supportingInfos}</div>
          ) : Array.isArray(errors.supportingInfos) ? (
            <div className="space-y-1">
              {errors.supportingInfos.map((error, index) => {
                if (error && typeof error === 'object') {
                  return (
                    <div key={index} className="flex flex-col">
                      <span className="font-semibold">Item {index + 1} ({values.supportingInfos[index]?.category}):</span>
                      {Object.entries(error).map(([field, message]) => (
                        <span key={field} className="ml-2">• {field}: {message}</span>
                      ))}
                    </div>
                  );
                } else if (error) {
                  return <div key={index}>Item {index + 1}: {error}</div>;
                }
                return null;
              })}
            </div>
          ) : (
            <div>{t("Please check all required supporting information fields")}</div>
          )}
        </div>
      )}

      <DentalTeethSelectModal
        showTeethSelectModal={showTeethSelectModal}
        setShowTeethSelectModal={setShowTeethSelectModal}
        setFieldValue={setFieldValue}
        index={supportingInfo}
        getRequiredItemName={getRequiredItemName}
        selectedTooth={values.supportingInfos?.[supportingInfo]?.code || null}
      />
    </div>
  );
}

export default SupportingInfoForm;
